/**
 * Utility functions to handle modal focus management and pointer events
 */

/**
 * Force restore pointer events on all elements that might have been disabled
 * This fixes the issue where Radix UI Dialog sets pointer-events: none and doesn't restore it
 */
export const restorePointerEvents = () => {
  // Find all elements with pointer-events: none that shouldn't have it
  const elementsWithNonePointerEvents = document.querySelectorAll("*");

  elementsWithNonePointerEvents.forEach((element) => {
    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.pointerEvents === "none") {
      // Check if this element should have pointer events (excluding overlays and disabled elements)
      const isDialogOverlay =
        element.hasAttribute("data-radix-dialog-overlay") ||
        element.classList.contains("dialog-overlay");
      const isDisabledElement =
        element.hasAttribute("disabled") ||
        element.getAttribute("aria-disabled") === "true";
      const isHiddenElement =
        element.style.display === "none" ||
        element.hidden ||
        computedStyle.visibility === "hidden";

      // If it's not an overlay, disabled, or hidden element, restore pointer events
      if (!isDialogOverlay && !isDisabledElement && !isHiddenElement) {
        // Check if the element had an inline style set to none
        if (element.style.pointerEvents === "none") {
          element.style.pointerEvents = "";
        }
      }
    }
  });
};

/**
 * Enhanced modal close handler that ensures proper cleanup
 */
export const createModalCloseHandler = (
  closeCallback,
  additionalCleanup = null
) => {
  return () => {
    // Execute the original close callback
    if (closeCallback) {
      closeCallback();
    }

    // Execute additional cleanup if provided
    if (additionalCleanup) {
      additionalCleanup();
    }

    // Use setTimeout to ensure the modal has started closing before restoring pointer events
    setTimeout(() => {
      restorePointerEvents();

      // Also try to restore focus to body if no element is focused
      if (!document.activeElement || document.activeElement === document.body) {
        // Find the first focusable element in the main content
        const mainContent = document.querySelector(
          'main, [role="main"], #root'
        );
        if (mainContent) {
          const focusableElement = mainContent.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElement) {
            focusableElement.focus();
          }
        }
      }
    }, 100);

    // Additional cleanup after longer delay to ensure all animations complete
    setTimeout(() => {
      restorePointerEvents();
    }, 300);
  };
};

/**
 * Monitor for pointer events issues and auto-fix them
 */
export const startPointerEventsMonitor = () => {
  // Check every 2 seconds for pointer events issues
  const intervalId = setInterval(() => {
    // Only run if no modal is currently open
    const openDialogs = document.querySelectorAll(
      '[data-state="open"][role="dialog"]'
    );
    if (openDialogs.length === 0) {
      restorePointerEvents();
    }
  }, 2000);

  return () => clearInterval(intervalId);
};

/**
 * Initialize pointer events monitoring when the app starts
 */
export const initializePointerEventsMonitoring = () => {
  // Start the monitor
  const stopMonitor = startPointerEventsMonitor();

  // Clean up on page unload
  window.addEventListener("beforeunload", stopMonitor);

  return stopMonitor;
};
